import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";

import api, { messageErreur, telecharger } from "../api";
import ChampSignature from "../components/ChampSignature";
import Layout from "../components/Layout";
import ModaleConfirmation from "../components/ModaleConfirmation";
import Notification from "../components/Notification";
import { SqueletteCartes } from "../components/Squelette";
import { formaterDate, formaterEuros, formaterKm, periodeInversee, MESSAGE_PERIODE_INVERSEE } from "../format";
import { useModules } from "../modules";

// Mettre un camion en location : un ASSISTANT, comme les inspections.
//
// DEUX ENTRÉES, UN SEUL ÉCRAN
//   /vehicules/:id/location/nouvelle  -> on saisit le locataire et les
//                                        conditions, ce qui OUVRE un brouillon ;
//   /locations/:id                    -> on reprend ce brouillon, on prolonge ou
//                                        on rend le camion, ou on relit un
//                                        contrat terminé.
//
// LE CYCLE : brouillon -> signée (en cours) -> restituée. À chaque étape l'écran
// ne montre que ce qui se peut faire :
//   - brouillon : locataire, conditions, état de départ, assurance, signatures ;
//   - en cours  : PROLONGER (un avenant signé) ou RESTITUER (état de retour signé) ;
//   - restituée : relire, ouvrir les PDF, RENOUVELER.
//
// CHAQUE SECTION S'ENREGISTRE SEULE. Un assistant qui n'enregistre qu'à la fin
// perd tout si le téléphone se met en veille dans la cabine — et c'est là qu'il
// sera utilisé, debout à côté du camion.
//
// LE SERVEUR TIENT LES RÈGLES (six photos par moment, signature obligatoire,
// documents figés). Ici on les ANNONCE : la liste « Il manque… » dit pourquoi un
// bouton est grisé, plutôt que de le laisser inexplicablement mort.

const MAX_PHOTOS = 6;

// LES COORDONNÉES DU LOUEUR figurent sur le contrat (bloc « Loueur ») : sans elles, le
// document porte des « - » là où un contrat attend une adresse et un SIRET. On AVERTIT,
// sans bloquer -- comme pour le justificatif d'assurance : empêcher de louer parce qu'une
// information manque ferait contourner le logiciel, la rendre visible suffit. Elles se
// saisissent UNE fois dans Réglages (« Mon entreprise »), et le brouillon est déjà
// enregistré : on peut aller les compléter et revenir.
//
// Seul l'administrateur y a accès (la route est la sienne) : on n'interroge pas pour les
// autres rôles, ce serait un appel voué au refus.
function AvertissementLoueur({ actif }) {
  const [manque, setManque] = useState([]);
  useEffect(() => {
    if (!actif) return;
    api
      .get("/reglages/entreprise")
      .then(({ data }) => {
        const absent = [];
        if (!data.adresse && !data.ville) absent.push("l'adresse");
        if (!data.siret) absent.push("le SIRET");
        setManque(absent);
      })
      // Muet : un avertissement qui n'arrive pas ne doit pas gêner la saisie.
      .catch(() => {});
  }, [actif]);
  if (manque.length === 0) return null;
  return (
    <p className="avertissement-loueur" data-testid="avertissement-loueur" role="status">
      Les coordonnées de votre entreprise sont incomplètes : il manque {manque.join(" et ")}. Elles
      figureront sur le contrat (bloc « Loueur »).{" "}
      <Link to="/reglages">Compléter dans Réglages</Link>
    </p>
  );
}
const heureMaintenant = () => new Date().toTimeString().slice(0, 5);
const aujourdhui = () => new Date().toISOString().slice(0, 10);

const LOCATAIRE_VIDE = {
  type_locataire: "particulier", societe: "", siret: "",
  nom: "", prenom: "", telephone: "", email: "", adresse: "",
};

function nettoyer(objet) {
  const sortie = {};
  Object.entries(objet).forEach(([k, v]) => {
    sortie[k] = typeof v === "string" && v.trim() === "" ? null : v;
  });
  return sortie;
}

// Une photo se charge en BLOB, jamais en <img src> direct : elle est servie par
// une route authentifiée, et une balise <img> n'envoie pas le jeton — elle
// recevrait 401 et afficherait une image cassée. Même motif que les photos
// d'inspection : le serveur aurait raison, l'écran serait faux.
function Vignette({ photoId, onSupprimer, peutSupprimer }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let courante = null;
    let annule = false;
    api
      .get(`/location-photos/${photoId}/fichier`, { responseType: "blob" })
      .then(({ data }) => {
        if (annule) return;
        courante = URL.createObjectURL(data);
        setUrl(courante);
      })
      .catch(() => {});
    return () => {
      annule = true;
      // Révoquée au démontage : sinon chaque rechargement laisse ses octets en
      // mémoire.
      if (courante) URL.revokeObjectURL(courante);
    };
  }, [photoId]);
  return (
    <div className="vignette-location">
      {url ? <img src={url} alt="État du véhicule" /> : <div className="vignette-vide" />}
      {peutSupprimer && (
        <button type="button" className="lien-danger" onClick={onSupprimer}>
          Supprimer
        </button>
      )}
    </div>
  );
}

// Le bloc de photos, le MÊME au départ et au retour : un seul code, donc un seul
// plafond, un seul message, une seule façon de charger une photo.
function BlocPhotos({ titre, photos, idChamp, onAjouter, onSupprimer }) {
  const champ = useRef(null);
  // La photo de la JAUGE a son propre bloc (BlocCarburant) mais compte dans les
  // six : le plafond est celui du serveur, par moment, jauge comprise.
  const normales = photos.filter((p) => !p.carburant);
  return (
    <>
      <h4>{titre} ({photos.length}/{MAX_PHOTOS})</h4>
      <p className="note-discrete">
        Tous les côtés, l'intérieur et le compteur. {MAX_PHOTOS} photos au maximum : inutile d'en prendre plus.
      </p>
      <div className="grille-vignettes">
        {normales.map((p) => (
          <Vignette key={p.id} photoId={p.id} peutSupprimer onSupprimer={() => onSupprimer(p)} />
        ))}
      </div>
      <input
        ref={champ} type="file" multiple hidden
        accept="image/jpeg,image/png,image/webp" onChange={onAjouter}
        data-testid={idChamp}
      />
      <button className="btn" disabled={photos.length >= MAX_PHOTOS} onClick={() => champ.current.click()}>
        + Ajouter des photos
      </button>
    </>
  );
}

// LA PHOTO DE LA JAUGE DE CARBURANT (demande du patron, 20/10) : elle prouve le
// niveau au départ comme au retour. Sans elle, facturer le gasoil au locataire
// serait contestable. Obligatoire pour signer et pour restituer.
function BlocCarburant({ photos, idChamp, onAjouter, onSupprimer }) {
  const champ = useRef(null);
  const jauge = photos.filter((p) => p.carburant);
  return (
    <>
      <h4>Jauge de carburant {jauge.length > 0 ? "✓" : "*"}</h4>
      <p className="note-discrete">
        Photographiez le tableau de bord, jauge visible : c'est elle qui fait foi du niveau de carburant.
      </p>
      <div className="grille-vignettes">
        {jauge.map((p) => (
          <Vignette key={p.id} photoId={p.id} peutSupprimer onSupprimer={() => onSupprimer(p)} />
        ))}
      </div>
      <input
        ref={champ} type="file" hidden accept="image/jpeg,image/png,image/webp"
        onChange={onAjouter} data-testid={idChamp}
      />
      <button
        className="btn" disabled={photos.length >= MAX_PHOTOS}
        onClick={() => champ.current.click()}
      >
        {jauge.length > 0 ? "+ Photographier à nouveau la jauge" : "+ Photographier la jauge"}
      </button>
    </>
  );
}

// « Le locataire doit-il rendre le camion plein ? » -- un CHOIX EXPLICITE, sans
// valeur cochée d'avance : un « non » par défaut ferait facturer le gasoil sans
// que personne l'ait voulu.
function ChoixPlein({ valeur, onChange }) {
  return (
    <fieldset className="champ champ-large choix-radio">
      <legend className="champ-label">Le locataire doit rendre le véhicule plein *</legend>
      <label>
        <input type="radio" name="plein" checked={valeur === "oui"} onChange={() => onChange("oui")} />{" "}
        Oui
      </label>{" "}
      <label>
        <input type="radio" name="plein" checked={valeur === "non"} onChange={() => onChange("non")} />{" "}
        Non : le gasoil lui est facturé
      </label>
    </fieldset>
  );
}

export default function LocationWizard({ user, onLogout }) {
  const { vehiculeId, locationId } = useParams();
  const [params] = useSearchParams();
  const renouvelerId = params.get("renouveler");
  const navigate = useNavigate();
  const modules = useModules();
  const estAdmin = user.role === "admin";

  const [loc, setLoc] = useState(null);
  const [chargement, setChargement] = useState(Boolean(locationId));
  const [erreur, setErreur] = useState("");
  const [message, setMessage] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  // Création
  const [vehicule, setVehicule] = useState(null);
  const [existants, setExistants] = useState([]);
  const [locataireId, setLocataireId] = useState("");
  const [locataire, setLocataire] = useState(LOCATAIRE_VIDE);
  const [precedente, setPrecedente] = useState(null);
  const [conditions, setConditions] = useState({
    date_debut: aujourdhui(), date_fin: "", loyer_mensuel: "", km_inclus_mensuel: "", notes: "",
    plein_a_rendre: "", // "oui" | "non" : choisi explicitement
  });
  // Reprise : départ, retour, prolongation
  const [depart, setDepart] = useState({ km: "", heure: heureMaintenant() });
  const [retour, setRetour] = useState({ km: "", heure: heureMaintenant(), date: aujourdhui() });
  const [avenant, setAvenant] = useState({ fin: "", loyer: "", motif: "" });
  const champAssurance = useRef(null);

  const annoncer = (texte) => {
    setMessage(texte);
    setTimeout(() => setMessage(""), 5000);
  };

  const charger = useCallback(async () => {
    if (!locationId) return;
    try {
      const { data } = await api.get(`/locations/${locationId}`);
      setLoc(data);
      setConditions({
        date_debut: data.date_debut, date_fin: data.date_fin,
        loyer_mensuel: data.loyer_mensuel, km_inclus_mensuel: data.km_inclus_mensuel ?? "",
        notes: data.notes || "",
        plein_a_rendre: data.plein_a_rendre ? "oui" : "non",
      });
      setDepart((d) => ({
        km: data.km_depart ?? d.km,
        heure: data.heure_depart ? data.heure_depart.slice(0, 5) : d.heure,
      }));
      setRetour((r) => ({
        km: data.km_retour ?? r.km,
        heure: data.heure_retour ? data.heure_retour.slice(0, 5) : r.heure,
        date: data.date_restitution || r.date,
      }));
      setErreur("");
    } catch (err) {
      setErreur(
        err?.response?.status === 404
          ? "Cette location est introuvable."
          : messageErreur(err, "Impossible de charger la location.")
      );
    } finally {
      setChargement(false);
    }
  }, [locationId]);

  useEffect(() => {
    charger();
  }, [charger]);

  useEffect(() => {
    if (locationId || !vehiculeId) return;
    api.get(`/vehicules/${vehiculeId}`).then(({ data }) => setVehicule(data)).catch(() => {});
    // Les locataires déjà saisis : un locataire revient (renouvellement, second camion).
    if (estAdmin) api.get("/locataires").then(({ data }) => setExistants(data)).catch(() => {});
  }, [locationId, vehiculeId, estAdmin]);

  // RENOUVELER : on repart de la location terminée — même locataire, mêmes
  // conditions — mais avec un NOUVEL état de départ : le camion est revenu, il a
  // pu être abîmé, et c'est tout l'objet de refaire photos et signature.
  useEffect(() => {
    if (locationId || !renouvelerId) return;
    api
      .get(`/locations/${renouvelerId}`)
      .then(({ data }) => {
        setPrecedente(data);
        setLocataireId(String(data.locataire_id));
        setConditions((c) => ({
          ...c,
          loyer_mensuel: data.loyer_mensuel,
          km_inclus_mensuel: data.km_inclus_mensuel ?? "",
          plein_a_rendre: data.plein_a_rendre ? "oui" : "non",
        }));
      })
      .catch(() => {});
  }, [locationId, renouvelerId]);

  // Le module n'est pas actif : le serveur répondrait 403 sur chaque appel. Ce
  // n'est PAS la protection — c'est `exiger_module` — seulement une façon de ne
  // pas afficher un écran incompréhensible.
  if (modules.charge && !modules.module_location) return <Navigate to="/vehicules" replace />;

  // ---------------------------------------------------------------- création
  const ouvrir = async (e) => {
    e.preventDefault();
    setErreur("");
    const manque = [];
    if (!locataireId && locataire.type_locataire === "entreprise" && !locataire.societe.trim()) manque.push("la raison sociale");
    if (!locataireId && (!locataire.nom.trim() || !locataire.prenom.trim())) {
      manque.push(locataire.type_locataire === "entreprise" ? "le nom et le prénom du représentant" : "le nom et le prénom du locataire");
    }
    if (!conditions.date_debut) manque.push("la date de début");
    if (!conditions.date_fin) manque.push("la date de fin");
    if (conditions.loyer_mensuel === "") manque.push("le loyer mensuel HT");
    if (conditions.plein_a_rendre === "") manque.push("si le locataire doit rendre le véhicule plein");
    if (manque.length) return setErreur(`Renseignez ${manque.join(", ")}.`);
    if (periodeInversee(conditions.date_debut, conditions.date_fin)) return setErreur(MESSAGE_PERIODE_INVERSEE);
    setEnvoi(true);
    try {
      const corps = {
        date_debut: conditions.date_debut, date_fin: conditions.date_fin,
        loyer_mensuel: conditions.loyer_mensuel,
        km_inclus_mensuel: conditions.km_inclus_mensuel === "" ? null : Number(conditions.km_inclus_mensuel),
        notes: conditions.notes.trim() || null,
        plein_a_rendre: conditions.plein_a_rendre === "oui",
        ...(locataireId ? { locataire_id: Number(locataireId) } : { locataire: nettoyer(locataire) }),
        ...(precedente ? { renouvelle_de_id: precedente.id } : {}),
      };
      const { data } = await api.post(`/vehicules/${vehiculeId}/locations`, corps);
      navigate(`/locations/${data.id}`, { replace: true });
    } catch (err) {
      setErreur(messageErreur(err, "Impossible d'ouvrir la location."));
    } finally {
      // REMIS A ZERO DANS TOUS LES CAS. Passer de /vehicules/:id/location/nouvelle
      // a /locations/:id garde le MEME composant monte : son etat survit a la
      // navigation. Remis a zero seulement en cas d'echec, « envoi » restait vrai
      // et le bouton « Signer » de l'etape suivante affichait « Signature… »,
      // grise pour toujours -- trouve par le parcours navigateur, pas par le
      // serveur, qui n'y est pour rien.
      setEnvoi(false);
    }
  };

  // ------------------------------------------------------- sections du brouillon
  const enregistrerConditions = async () => {
    setErreur("");
    if (periodeInversee(conditions.date_debut, conditions.date_fin)) return setErreur(MESSAGE_PERIODE_INVERSEE);
    try {
      const { data } = await api.put(`/locations/${loc.id}`, {
        date_debut: conditions.date_debut, date_fin: conditions.date_fin,
        loyer_mensuel: conditions.loyer_mensuel,
        km_inclus_mensuel: conditions.km_inclus_mensuel === "" ? null : Number(conditions.km_inclus_mensuel),
        notes: conditions.notes.trim() || null,
        ...(conditions.plein_a_rendre !== "" ? { plein_a_rendre: conditions.plein_a_rendre === "oui" } : {}),
      });
      setLoc(data);
      annoncer("Conditions enregistrées.");
    } catch (err) {
      setErreur(messageErreur(err, "Enregistrement impossible."));
    }
  };

  const enregistrerDepart = async () => {
    setErreur("");
    if (depart.km === "") return setErreur("Renseignez le kilométrage au compteur.");
    try {
      const { data } = await api.put(`/locations/${loc.id}`, {
        km_depart: Number(depart.km), heure_depart: `${depart.heure || heureMaintenant()}:00`,
      });
      setLoc(data);
      annoncer("Kilométrage enregistré.");
    } catch (err) {
      setErreur(messageErreur(err, "Enregistrement impossible."));
    }
  };

  // Les photos du départ et celles du retour partagent le même geste : seul le
  // « moment » change, et le serveur tient le plafond de six pour chacun.
  const ajouterPhotos = (moment, carburant = false) => async (e) => {
    const fichiers = Array.from(e.target.files || []);
    e.target.value = "";
    if (!fichiers.length) return;
    const existantes = moment === "retour" ? loc.photos_retour : loc.photos;
    const restantes = MAX_PHOTOS - existantes.length;
    if (fichiers.length > restantes) {
      return setErreur(
        restantes > 0
          ? `${MAX_PHOTOS} photos au maximum : vous pouvez encore en ajouter ${restantes}.`
          : `${MAX_PHOTOS} photos au maximum : supprimez-en une pour en ajouter une autre.`
      );
    }
    setErreur("");
    const donnees = new FormData();
    fichiers.forEach((f) => donnees.append("files", f));
    try {
      await api.post(
        `/locations/${loc.id}/photos?moment=${moment}${carburant ? "&carburant=true" : ""}`, donnees);
      await charger();
    } catch (err) {
      setErreur(messageErreur(err, "Envoi des photos impossible."));
    }
  };

  const supprimerPhoto = (photo) =>
    setConfirmation({
      titre: "Supprimer cette photo ?",
      libelleConfirmer: "Supprimer",
      danger: true,
      onConfirmer: async () => {
        await api.delete(`/location-photos/${photo.id}`);
        await charger();
      },
    });

  const deposerJustificatif = async (e) => {
    const fichier = e.target.files?.[0];
    e.target.value = "";
    if (!fichier) return;
    setErreur("");
    const donnees = new FormData();
    donnees.append("fichier", fichier);
    try {
      const { data } = await api.post(`/locations/${loc.id}/assurance`, donnees);
      setLoc(data);
      annoncer("Justificatif enregistré.");
    } catch (err) {
      setErreur(messageErreur(err, "Envoi du justificatif impossible."));
    }
  };

  const retirerJustificatif = async () => {
    try {
      await api.delete(`/locations/${loc.id}/assurance`);
      await charger();
    } catch (err) {
      setErreur(messageErreur(err, "Suppression impossible."));
    }
  };

  const signer = (chemin) => async (blob) => {
    const donnees = new FormData();
    donnees.append("fichier", blob, "signature.png");
    try {
      const { data } = await api.post(`/locations/${loc.id}/${chemin}`, donnees);
      setLoc(data);
    } catch (err) {
      // Relancée pour que le champ affiche l'échec sous le trait.
      throw new Error(messageErreur(err, "Enregistrement de la signature impossible."));
    }
  };

  const manquant = loc && loc.statut === "brouillon" ? [
    loc.km_depart == null && "le kilométrage de départ",
    loc.photos.filter((p) => !p.carburant).length === 0 && "au moins une photo",
    !loc.photos.some((p) => p.carburant) && "la photo de la jauge de carburant",
    !loc.signature_locataire_posee && "la signature du locataire",
  ].filter(Boolean) : [];

  const finaliser = async () => {
    setErreur("");
    setEnvoi(true);
    try {
      const { data } = await api.post(`/locations/${loc.id}/finaliser`, {
        heure_depart: `${depart.heure || heureMaintenant()}:00`,
      });
      setLoc(data);
      annoncer("Contrat signé. Il est archivé dans les documents du véhicule.");
    } catch (err) {
      setErreur(messageErreur(err, "Impossible de signer le contrat."));
    } finally {
      setEnvoi(false);
    }
  };

  const annuler = () =>
    setConfirmation({
      titre: "Annuler ce brouillon ?",
      message: "Les photos et signatures saisies sont supprimées, et le véhicule redevient disponible.",
      libelleConfirmer: "Annuler le brouillon",
      libelleAnnuler: "Continuer la saisie",
      danger: true,
      onConfirmer: async () => {
        await api.delete(`/locations/${loc.id}`);
        navigate(`/vehicules/${loc.vehicule_id}`, { replace: true });
      },
    });

  const ouvrirDocument = (documentId, nom) =>
    telecharger(`/documents/${documentId}/fichier`, nom).catch(() =>
      setErreur("Impossible d'ouvrir le document.")
    );

  // ------------------------------------------------- location en cours : retour
  const enregistrerRetour = async () => {
    setErreur("");
    if (retour.km === "") return setErreur("Renseignez le kilométrage au compteur.");
    try {
      const { data } = await api.put(`/locations/${loc.id}/retour`, {
        km_retour: Number(retour.km), heure_retour: `${retour.heure || heureMaintenant()}:00`,
        date_restitution: retour.date,
      });
      setLoc(data);
      annoncer("Retour enregistré.");
    } catch (err) {
      setErreur(messageErreur(err, "Enregistrement impossible."));
    }
  };

  const manquantRetour = loc && loc.statut === "signee" ? [
    loc.km_retour == null && "le kilométrage au retour",
    loc.photos_retour.filter((p) => !p.carburant).length === 0 && "au moins une photo",
    !loc.photos_retour.some((p) => p.carburant) && "la photo de la jauge de carburant",
    !loc.signature_retour_locataire_posee && "la signature du locataire",
  ].filter(Boolean) : [];

  const restituer = async () => {
    setErreur("");
    setEnvoi(true);
    try {
      const { data } = await api.post(`/locations/${loc.id}/restituer`);
      setLoc(data);
      annoncer("Véhicule restitué. L'état de retour est archivé dans les documents du véhicule.");
    } catch (err) {
      setErreur(messageErreur(err, "Impossible de restituer le véhicule."));
    } finally {
      setEnvoi(false);
    }
  };

  // ------------------------------------------------- location en cours : avenant
  // L'enregistrement de la signature EST l'envoi de tout l'avenant : date, loyer,
  // motif et trait arrivent ensemble, et le serveur produit le PDF dans la même
  // transaction. Pas d'avenant « à moitié signé ».
  const prolonger = async (blob) => {
    if (!avenant.fin) throw new Error("Indiquez la nouvelle date de fin.");
    if (avenant.fin <= loc.date_fin) {
      throw new Error(`Une prolongation repousse la fin : la nouvelle date doit suivre le ${formaterDate(loc.date_fin)}.`);
    }
    const donnees = new FormData();
    donnees.append("nouvelle_date_fin", avenant.fin);
    if (avenant.loyer !== "") donnees.append("nouveau_loyer", avenant.loyer);
    if (avenant.motif.trim()) donnees.append("motif", avenant.motif.trim());
    donnees.append("signature_locataire", blob, "signature.png");
    try {
      const { data } = await api.post(`/locations/${loc.id}/avenants`, donnees);
      setLoc(data);
      setConditions((c) => ({ ...c, date_fin: data.date_fin, loyer_mensuel: data.loyer_mensuel }));
      setAvenant({ fin: "", loyer: "", motif: "" });
      annoncer("Prolongation signée. L'avenant est archivé dans les documents du véhicule.");
    } catch (err) {
      throw new Error(messageErreur(err, "Impossible de signer l'avenant."));
    }
  };

  // ----------------------------------------------------------------- rendu
  const titre = loc ? `Location — ${loc.immatriculation}` : vehicule ? `Mettre en location — ${vehicule.immatriculation}` : "Location";

  return (
    <Layout user={user} onLogout={onLogout}>
      <Notification message={message} />
      {confirmation && (
        <ModaleConfirmation {...confirmation} onFermer={() => setConfirmation(null)} />
      )}
      <div className="entete-page">
        <button
          className="btn"
          onClick={() => navigate(vehiculeId || loc ? `/vehicules/${vehiculeId || loc.vehicule_id}` : "/vehicules")}
        >
          ← Retour au véhicule
        </button>
        <h2>{titre}</h2>
      </div>

      {chargement && <SqueletteCartes nombre={3} />}
      {erreur && <div className="erreur" role="alert">{erreur}</div>}

      {/* ---------- Création : locataire + conditions ---------- */}
      {!locationId && estAdmin && (
        <form className="formulaire" onSubmit={ouvrir} noValidate>
          <AvertissementLoueur actif={estAdmin} />
          {precedente && (
            <p className="note-discrete" data-testid="renouvellement">
              Renouvellement de la location terminée le {formaterDate(precedente.date_restitution)} : le locataire et
              les conditions sont repris. L'état de départ est à refaire.
            </p>
          )}
          <section className="carte">
            <h3>1. Le locataire</h3>
            {existants.length > 0 && (
              <label className="champ">
                <span className="champ-label">Locataire déjà saisi</span>
                <select value={locataireId} onChange={(e) => setLocataireId(e.target.value)}>
                  <option value="">— Nouveau locataire —</option>
                  {existants.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.type_locataire === "entreprise" && l.societe ? `${l.societe} (${l.prenom} ${l.nom})` : `${l.prenom} ${l.nom}`}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {!locataireId && (
              <>
                {/* PARTICULIER OU ENTREPRISE (demande du patron, 20/10) : une
                    entreprise montre sa raison sociale et son SIRET, et la
                    personne saisie dessous est son REPRÉSENTANT. */}
                <fieldset className="champ champ-large choix-radio">
                  <legend className="champ-label">Le locataire est</legend>
                  {[["particulier", "Un particulier"], ["entreprise", "Une entreprise"]].map(([valeur, libelle]) => (
                    <label key={valeur}>
                      <input
                        type="radio" name="type_locataire"
                        checked={locataire.type_locataire === valeur}
                        onChange={() => setLocataire({ ...locataire, type_locataire: valeur })}
                      />{" "}
                      {libelle}{" "}
                    </label>
                  ))}
                </fieldset>
                <div className="grille-form">
                  {(locataire.type_locataire === "entreprise"
                    ? [["societe", "Société *"], ["siret", "SIRET"],
                       ["prenom", "Prénom du représentant *"], ["nom", "Nom du représentant *"]]
                    : [["prenom", "Prénom *"], ["nom", "Nom *"]]
                  ).concat([["telephone", "Téléphone"], ["email", "E-mail"], ["adresse", "Adresse"]])
                    .map(([cle, libelle]) => (
                      <label className="champ" key={cle}>
                        <span className="champ-label">{libelle}</span>
                        <input
                          value={locataire[cle]}
                          inputMode={cle === "siret" ? "numeric" : undefined}
                          onChange={(e) => setLocataire({ ...locataire, [cle]: e.target.value })}
                        />
                      </label>
                    ))}
                </div>
              </>
            )}
          </section>
          <section className="carte">
            <h3>2. Les conditions</h3>
            <ChampsConditions valeurs={conditions} onChange={setConditions} />
          </section>
          <div className="actions-form">
            <button type="submit" className="btn-accent" disabled={envoi}>
              {envoi ? "Ouverture…" : "Continuer : état de départ"}
            </button>
          </div>
        </form>
      )}
      {!locationId && !estAdmin && (
        <p className="vide">Seul un administrateur peut mettre un véhicule en location.</p>
      )}

      {/* ---------- Brouillon ---------- */}
      {loc && loc.statut === "brouillon" && !estAdmin && (
        <>
          <p className="vide">Location en préparation. Seul un administrateur peut la compléter.</p>
          <Recapitulatif loc={loc} />
        </>
      )}
      {loc && loc.statut === "brouillon" && estAdmin && (
        <>
          <section className="carte">
            <h3>Locataire et conditions</h3>
            {loc.renouvelle_de_id && (
              <p className="note-discrete">Renouvellement d'une location précédente.</p>
            )}
            {loc.locataire_detail && (
              <p className="note-discrete">
                {loc.locataire_detail.type_locataire === "entreprise"
                  ? `${loc.locataire_detail.societe}${loc.locataire_detail.siret ? ` (SIRET ${loc.locataire_detail.siret})` : ""} — représentée par `
                  : ""}
                {loc.locataire_detail.prenom} {loc.locataire_detail.nom}
                {loc.locataire_detail.telephone ? ` · ${loc.locataire_detail.telephone}` : ""}
              </p>
            )}
            <ChampsConditions valeurs={conditions} onChange={setConditions} />
            <div className="actions-form">
              <button className="btn" onClick={enregistrerConditions}>Enregistrer les conditions</button>
            </div>
          </section>

          <section className="carte">
            <h3>3. État de départ</h3>
            <div className="grille-form">
              <label className="champ">
                <span className="champ-label">Kilométrage au compteur *</span>
                <input
                  type="number" inputMode="numeric" min="0" value={depart.km}
                  onChange={(e) => setDepart({ ...depart, km: e.target.value })}
                />
              </label>
              <label className="champ">
                <span className="champ-label">Heure de la remise</span>
                <input
                  type="time" value={depart.heure}
                  onChange={(e) => setDepart({ ...depart, heure: e.target.value })}
                />
              </label>
            </div>
            <div className="actions-form">
              <button className="btn" onClick={enregistrerDepart}>Enregistrer le kilométrage</button>
            </div>
            <BlocPhotos
              titre="Photos" photos={loc.photos} idChamp="champ-photos"
              onAjouter={ajouterPhotos("depart")} onSupprimer={supprimerPhoto}
            />
            <BlocCarburant
              photos={loc.photos} idChamp="champ-carburant"
              onAjouter={ajouterPhotos("depart", true)} onSupprimer={supprimerPhoto}
            />
          </section>

          <section className="carte">
            <h3>4. Justificatif d'assurance</h3>
            <p className="note-discrete">
              À fournir par le locataire. Il ne bloque pas la signature : le contrat indiquera « fourni » ou « NON FOURNI ».
            </p>
            {loc.assurance_fournie ? (
              <p>
                ✓ {loc.justificatif_nom || "Justificatif enregistré"}{" "}
                <button className="lien-action" onClick={() => telecharger(`/locations/${loc.id}/assurance`, "assurance.pdf")}>
                  Ouvrir
                </button>{" "}
                <button className="lien-danger" onClick={retirerJustificatif}>Retirer</button>
              </p>
            ) : (
              <p className="note-discrete">Aucun justificatif pour l'instant.</p>
            )}
            <input
              ref={champAssurance} type="file" hidden
              accept="application/pdf,image/jpeg,image/png,image/webp" onChange={deposerJustificatif}
              data-testid="champ-assurance"
            />
            <button className="btn" onClick={() => champAssurance.current.click()}>
              {loc.assurance_fournie ? "Remplacer le justificatif" : "+ Joindre le justificatif"}
            </button>
          </section>

          <section className="carte">
            <h3>5. Signatures</h3>
            <p className="note-discrete">
              Signature électronique simple : un tracé à l'écran. Elle suffit pour un état des lieux entre deux parties
              qui se connaissent ; elle prouve moins qu'un original papier devant un tribunal.
            </p>
            <ChampSignature
              libelle="Le locataire *" posee={loc.signature_locataire_posee}
              onEnregistrer={signer("signature/locataire")}
            />
            <ChampSignature
              libelle="Le loueur (facultatif)" posee={loc.signature_loueur_posee}
              onEnregistrer={signer("signature/loueur")}
            />
          </section>

          <section className="carte">
            <h3>6. Signer le contrat</h3>
            <AvertissementLoueur actif={estAdmin} />
            {manquant.length > 0 ? (
              <p className="note-discrete" data-testid="manque">
                Pour signer, il manque : {manquant.join(", ")}.
              </p>
            ) : (
              <p className="note-discrete">
                Tout est prêt. Une fois signé, le contrat est figé : il ne se modifie plus.
              </p>
            )}
            <div className="actions-groupe">
              <button className="btn-accent" disabled={manquant.length > 0 || envoi} onClick={finaliser}>
                {envoi ? "Signature…" : "Signer et générer le contrat"}
              </button>
              <button className="lien-danger" onClick={annuler}>Annuler ce brouillon</button>
            </div>
          </section>
        </>
      )}

      {/* ---------- Contrat signé (en cours) ---------- */}
      {loc && loc.statut === "signee" && (
        <>
          <section className="carte">
            <h3>Contrat signé — location en cours</h3>
            <p>
              Signé le {formaterDate(loc.date_signature)}. Fin prévue le <strong>{formaterDate(loc.date_fin)}</strong>
              {loc.jours_avant_fin != null && loc.jours_avant_fin < 0 && " (dépassée : le véhicule n'est pas rendu)"}.
            </p>
            {loc.document_contrat_id && (
              <button className="btn-accent" onClick={() => ouvrirDocument(loc.document_contrat_id, "contrat-location.pdf")}>
                Ouvrir le contrat (PDF)
              </button>
            )}
          </section>
          <Avenants loc={loc} onOuvrir={ouvrirDocument} />

          {estAdmin && (
            <>
              <section className="carte">
                <h3>Prolonger la location</h3>
                <p className="note-discrete">
                  Un avenant signé repousse la fin. Il s'ajoute au contrat sans rien écraser, et son PDF est archivé.
                </p>
                <div className="grille-form">
                  <label className="champ">
                    <span className="champ-label">Nouvelle date de fin *</span>
                    <input
                      type="date" value={avenant.fin} min={loc.date_fin}
                      onChange={(e) => setAvenant({ ...avenant, fin: e.target.value })}
                    />
                  </label>
                  <label className="champ">
                    <span className="champ-label">Nouveau loyer mensuel HT (€)</span>
                    <input
                      type="number" inputMode="decimal" min="0" step="0.01"
                      placeholder={`Inchangé : ${loc.loyer_mensuel}`}
                      value={avenant.loyer} onChange={(e) => setAvenant({ ...avenant, loyer: e.target.value })}
                    />
                  </label>
                  <label className="champ champ-large">
                    <span className="champ-label">Motif</span>
                    <input value={avenant.motif} onChange={(e) => setAvenant({ ...avenant, motif: e.target.value })} />
                  </label>
                </div>
                <ChampSignature
                  libelle="Le locataire *" posee={false}
                  libelleBouton="Signer l'avenant" onEnregistrer={prolonger}
                />
              </section>

              <section className="carte">
                <h3>Restituer le véhicule</h3>
                <p className="note-discrete">
                  Le locataire rend le camion : on relève le kilométrage, on photographie l'état, on signe. La location
                  est alors terminée et le véhicule redevient disponible.
                </p>
                <div className="grille-form">
                  <label className="champ">
                    <span className="champ-label">Kilométrage au retour *</span>
                    <input
                      type="number" inputMode="numeric" min="0" value={retour.km}
                      onChange={(e) => setRetour({ ...retour, km: e.target.value })}
                    />
                  </label>
                  <label className="champ">
                    <span className="champ-label">Date du retour</span>
                    <input
                      type="date" value={retour.date} max={aujourdhui()}
                      onChange={(e) => setRetour({ ...retour, date: e.target.value })}
                    />
                  </label>
                  <label className="champ">
                    <span className="champ-label">Heure du retour</span>
                    <input
                      type="time" value={retour.heure}
                      onChange={(e) => setRetour({ ...retour, heure: e.target.value })}
                    />
                  </label>
                </div>
                <div className="actions-form">
                  <button className="btn" onClick={enregistrerRetour}>Enregistrer le retour</button>
                </div>
                <BlocPhotos
                  titre="Photos de retour" photos={loc.photos_retour} idChamp="champ-photos-retour"
                  onAjouter={ajouterPhotos("retour")} onSupprimer={supprimerPhoto}
                />
                <BlocCarburant
                  photos={loc.photos_retour} idChamp="champ-carburant-retour"
                  onAjouter={ajouterPhotos("retour", true)} onSupprimer={supprimerPhoto}
                />
                <p className="note-discrete">
                  {loc.plein_a_rendre
                    ? "Condition du contrat : le locataire rend le véhicule plein."
                    : "Condition du contrat : plein non exigé, le gasoil est facturé au locataire."}
                </p>
                <ChampSignature
                  libelle="Le locataire *" posee={loc.signature_retour_locataire_posee}
                  onEnregistrer={signer("retour/signature/locataire")}
                />
                {manquantRetour.length > 0 ? (
                  <p className="note-discrete" data-testid="manque-retour">
                    Pour restituer, il manque : {manquantRetour.join(", ")}.
                  </p>
                ) : (
                  <p className="note-discrete">Tout est prêt. Une fois restitué, l'état de retour est figé.</p>
                )}
                <div className="actions-groupe">
                  <button className="btn-accent" disabled={manquantRetour.length > 0 || envoi} onClick={restituer}>
                    {envoi ? "Restitution…" : "Restituer le véhicule"}
                  </button>
                </div>
              </section>
            </>
          )}
          <Recapitulatif loc={loc} />
        </>
      )}

      {/* ---------- Location terminée ---------- */}
      {loc && loc.statut === "restituee" && (
        <>
          <section className="carte">
            <h3>Location terminée</h3>
            <p>
              Véhicule restitué le <strong>{formaterDate(loc.date_restitution)}</strong>
              {loc.heure_retour ? ` à ${loc.heure_retour.slice(0, 5)}` : ""}.
            </p>
            <dl className="liste-donnees" data-testid="bilan-km">
              <dt>Kilométrage au départ</dt><dd>{formaterKm(loc.km_depart)}</dd>
              <dt>Kilométrage au retour</dt><dd>{formaterKm(loc.km_retour)}</dd>
              <dt>Kilométrage parcouru</dt><dd>{formaterKm(loc.km_parcourus)}</dd>
              {loc.km_forfait_estime != null && (
                <>
                  <dt>Forfait estimé</dt>
                  <dd>{formaterKm(loc.km_forfait_estime)} <span className="note-discrete">(estimation, au prorata de la durée)</span></dd>
                  <dt>Écart</dt>
                  <dd>
                    {loc.km_ecart_estime > 0
                      ? `Dépassement estimé : ${formaterKm(loc.km_ecart_estime)}`
                      : "Dans le forfait"}
                  </dd>
                </>
              )}
            </dl>
            <p className="note-discrete">Le dépassement éventuel se règle entre les parties, hors du logiciel.</p>
            <div className="actions-groupe">
              {loc.document_contrat_id && (
                <button className="btn" onClick={() => ouvrirDocument(loc.document_contrat_id, "contrat-location.pdf")}>
                  Contrat de départ (PDF)
                </button>
              )}
              {loc.document_retour_id && (
                <button className="btn-accent" onClick={() => ouvrirDocument(loc.document_retour_id, "restitution.pdf")}>
                  État de retour (PDF)
                </button>
              )}
              {estAdmin && (
                <button
                  className="btn"
                  onClick={() => navigate(`/vehicules/${loc.vehicule_id}/location/nouvelle?renouveler=${loc.id}`)}
                >
                  Renouveler la location
                </button>
              )}
            </div>
          </section>
          <Avenants loc={loc} onOuvrir={ouvrirDocument} />
          <Recapitulatif loc={loc} />
        </>
      )}
    </Layout>
  );
}

function ChampsConditions({ valeurs, onChange }) {
  const maj = (cle) => (e) => onChange({ ...valeurs, [cle]: e.target.value });
  return (
    <div className="grille-form">
      <label className="champ">
        <span className="champ-label">Début *</span>
        <input type="date" value={valeurs.date_debut} onChange={maj("date_debut")} />
      </label>
      <label className="champ">
        <span className="champ-label">Fin prévue *</span>
        <input type="date" value={valeurs.date_fin} onChange={maj("date_fin")} />
      </label>
      <label className="champ">
        <span className="champ-label">Loyer mensuel HT (€) *</span>
        <input
          type="number" inputMode="decimal" min="0" step="0.01"
          value={valeurs.loyer_mensuel} onChange={maj("loyer_mensuel")}
        />
      </label>
      <label className="champ">
        <span className="champ-label">Kilométrage inclus par mois</span>
        <input
          type="number" inputMode="numeric" min="0" placeholder="Ex. 1000"
          value={valeurs.km_inclus_mensuel} onChange={maj("km_inclus_mensuel")}
        />
      </label>
      <ChoixPlein
        valeur={valeurs.plein_a_rendre}
        onChange={(v) => onChange({ ...valeurs, plein_a_rendre: v })}
      />
      <label className="champ champ-large">
        <span className="champ-label">Observations</span>
        <input value={valeurs.notes} onChange={maj("notes")} />
      </label>
    </div>
  );
}

// Les prolongations déjà signées : chacune garde son ancien état (fin, loyer) et
// son PDF. C'est l'historique qui dit pourquoi un contrat d'un mois en a duré six.
function Avenants({ loc, onOuvrir }) {
  if (!loc.avenants || loc.avenants.length === 0) return null;
  return (
    <section className="carte">
      <h3>Prolongations ({loc.avenants.length})</h3>
      <ul className="liste-simple">
        {loc.avenants.map((a) => (
          <li key={a.id}>
            Signée le {formaterDate(a.date_signature)} : fin du {formaterDate(a.ancienne_date_fin)} au{" "}
            <strong>{formaterDate(a.nouvelle_date_fin)}</strong>
            {Number(a.nouveau_loyer) !== Number(a.ancien_loyer)
              && ` · loyer ${formaterEuros(a.ancien_loyer)} → ${formaterEuros(a.nouveau_loyer)}`}
            {a.motif ? ` · ${a.motif}` : ""}{" "}
            {a.document_id && (
              <button className="lien-action" onClick={() => onOuvrir(a.document_id, "avenant-location.pdf")}>
                Ouvrir l'avenant
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Recapitulatif({ loc }) {
  return (
    <section className="carte">
      <h3>Récapitulatif</h3>
      <dl className="liste-donnees">
        <dt>Locataire</dt><dd>{loc.locataire}</dd>
        {loc.locataire_detail?.telephone && (<><dt>Téléphone</dt><dd>{loc.locataire_detail.telephone}</dd></>)}
        <dt>Période</dt><dd>du {formaterDate(loc.date_debut)} au {formaterDate(loc.date_fin)}</dd>
        <dt>Loyer mensuel HT</dt><dd>{formaterEuros(loc.loyer_mensuel)}</dd>
        <dt>Plein de carburant</dt>
        <dd>{loc.plein_a_rendre ? "Le locataire rend le véhicule plein" : "Non exigé : gasoil facturé au locataire"}</dd>
        <dt>Kilométrage inclus</dt>
        <dd>{loc.km_inclus_mensuel ? `${formaterKm(loc.km_inclus_mensuel)} par mois` : "Non précisé"}</dd>
        <dt>Kilométrage au départ</dt><dd>{formaterKm(loc.km_depart)}</dd>
        <dt>Justificatif d'assurance</dt><dd>{loc.assurance_fournie ? "Fourni" : "Non fourni"}</dd>
        <dt>Photos de départ</dt><dd>{loc.photos.length}</dd>
      </dl>
    </section>
  );
}
